import {
  ChangeEventHandler,
  FC,
  FocusEventHandler,
  InputHTMLAttributes,
  LegacyRef,
  ReactElement,
  SyntheticEvent,
  useState
} from 'react';
import ReactInputMask from 'react-input-mask';
import cn from 'classnames';
import { InputModeType, IMaskedProps } from 'core/types';
import { ErrorMessage } from '../../common/ErrorMessage';

export interface ITextField
  extends Omit<Partial<InputHTMLAttributes<HTMLInputElement>>, 'onChange'> {
  name: string;
  parsers?: Array<(value: string, name: string) => string>;
  onChange?: (name: string, value: any, e: SyntheticEvent<any>) => void;
  label?: string | ReactElement;
  inputMode?: InputModeType;
  error?: string;
  maskedProps?: IMaskedProps;
  inputRef?: LegacyRef<ReactInputMask> | undefined;
  classNames?: {
    root?: string;
    label?: string;
    input?: string;
    error?: string;
  };
}

export const TextField: FC<ITextField> = (props) => {
  const {
    onChange,
    parsers,
    onBlur,
    onFocus,
    label,
    name,
    error,
    classNames,
    maskedProps,
    inputRef,
    value,
    children,
    ...rest
  } = props;

  const [isFocus, setIsFocus] = useState<boolean>(false);

  const isEmpty = !!`${props.value || ''}`.length || isFocus;

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    onBlur && onBlur(e);
    setIsFocus(false);
  };

  const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    onFocus && onFocus(e);
    setIsFocus(true);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const val = parsers
      ? parsers.reduce(
        (acc: string, cur: (v: string, name: string) => string): string => cur(acc, name),
        e.target.value
      )
      : e.target.value;
    onChange && onChange(name, val, e);
  };

  return (
    <div
      className={cn(
        'input-box',
        { 'input-box__invalid': !!error },
        { 'input-box__active': isFocus },
        { 'input-box__no-empty': isEmpty },
        { 'input-box__disabled': props.disabled },
        classNames?.root
      )}
    >
      {children}
      {label && (
        <label className={cn('input-box__label', classNames?.label)} htmlFor={name}>
          {label}
        </label>
      )}
      {maskedProps && (
        <ReactInputMask
          value={value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className={cn(classNames?.input)}
          name={name}
          {...rest}
          {...maskedProps}
        />
      )}
      {error && <ErrorMessage classError={classNames?.error} message={error}/>}
    </div>
  );
};

TextField.defaultProps = {
  inputMode: 'text',
  maskedProps: {
    mask: ''
  }
};
